/**
 * ====================================================================
 * FIKO GD&T (Geometric Dimensioning & Tolerancing) TYPE DEFINITIONS
 * ====================================================================
 *
 * ASME Y14.5-2018 ve ISO 1101:2017 standartlarına tam uyumlu
 * Geometric Dimensioning and Tolerancing tip tanımları.
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 * @standard ASME Y14.5-2018, ISO 1101:2017
 */

import * as THREE from 'three';

// ====================================================================
// ASME Y14.5-2018 GEOMETRİK KARAKTERİSTİK SEMBOLLERİ
// ====================================================================

/**
 * Geometrik Karakteristik Tipleri - ASME Y14.5-2018
 *
 * Not: ASME Y14.5-2018'de Concentricity ve Symmetry kaldırıldı.
 * Bunlar artık Position toleransı ile kontrol edilmeli.
 */
export enum GDTCharacteristicType {
    // ═══════════════════════════════════════════════════════════════
    // FORM TOLERANCES (Şekil Toleransları) - Datum gerektirmez
    // ═══════════════════════════════════════════════════════════════

    /** Düzlük - Bir yüzeyin düzleme uygunluğu */
    FLATNESS = 'FLATNESS',

    /** Doğruluk - Bir çizginin doğruluğu */
    STRAIGHTNESS = 'STRAIGHTNESS',

    /** Dairesellik/Yuvarlaklık - Bir kesitteki dairesellik */
    CIRCULARITY = 'CIRCULARITY',

    /** Silindiriklik - Bir silindirin mükemmelliği */
    CYLINDRICITY = 'CYLINDRICITY',

    // ═══════════════════════════════════════════════════════════════
    // PROFILE TOLERANCES (Profil Toleransları)
    // ═══════════════════════════════════════════════════════════════

    /** Çizgi Profili - 2D profil toleransı */
    PROFILE_OF_LINE = 'PROFILE_OF_LINE',

    /** Yüzey Profili - 3D yüzey toleransı */
    PROFILE_OF_SURFACE = 'PROFILE_OF_SURFACE',

    // ═══════════════════════════════════════════════════════════════
    // ORIENTATION TOLERANCES (Yönelim Toleransları) - Datum gerektirir
    // ═══════════════════════════════════════════════════════════════

    /** Açısallık - Belirli bir açıda yönelim */
    ANGULARITY = 'ANGULARITY',

    /** Diklik - 90° yönelim */
    PERPENDICULARITY = 'PERPENDICULARITY',

    /** Paralellik - Paralel yönelim */
    PARALLELISM = 'PARALLELISM',

    // ═══════════════════════════════════════════════════════════════
    // LOCATION TOLERANCES (Konum Toleransları) - Datum gerektirir
    // ═══════════════════════════════════════════════════════════════

    /** Konum - True position toleransı */
    POSITION = 'POSITION',

    /**
     * Eş Merkezlilik - ASME Y14.5-2018'de DEPRECATED
     * @deprecated Position kullanın
     */
    CONCENTRICITY = 'CONCENTRICITY',

    /**
     * Simetri - ASME Y14.5-2018'de DEPRECATED
     * @deprecated Position kullanın
     */
    SYMMETRY = 'SYMMETRY',

    // ═══════════════════════════════════════════════════════════════
    // RUNOUT TOLERANCES (Kaçıklık Toleransları) - Datum gerektirir
    // ═══════════════════════════════════════════════════════════════

    /** Dairesel Kaçıklık - Tek kesitte kaçıklık */
    CIRCULAR_RUNOUT = 'CIRCULAR_RUNOUT',

    /** Toplam Kaçıklık - Tüm yüzey boyunca kaçıklık */
    TOTAL_RUNOUT = 'TOTAL_RUNOUT'
}

/**
 * GD&T Sembol Unicode Karakterleri
 * ASME Y14.5-2018 standart sembolleri
 */
export const GDT_SYMBOLS: Record<GDTCharacteristicType, string> = {
    // Form
    [GDTCharacteristicType.FLATNESS]: '⏥',        // U+23E5
    [GDTCharacteristicType.STRAIGHTNESS]: '⏤',    // U+23E4
    [GDTCharacteristicType.CIRCULARITY]: '○',     // U+25CB
    [GDTCharacteristicType.CYLINDRICITY]: '⌭',    // U+232D

    // Profile
    [GDTCharacteristicType.PROFILE_OF_LINE]: '⌒',    // U+2312
    [GDTCharacteristicType.PROFILE_OF_SURFACE]: '⌓', // U+2313

    // Orientation
    [GDTCharacteristicType.ANGULARITY]: '∠',      // U+2220
    [GDTCharacteristicType.PERPENDICULARITY]: '⟂', // U+27C2
    [GDTCharacteristicType.PARALLELISM]: '∥',     // U+2225

    // Location
    [GDTCharacteristicType.POSITION]: '⌖',        // U+2316
    [GDTCharacteristicType.CONCENTRICITY]: '◎',   // U+25CE (deprecated)
    [GDTCharacteristicType.SYMMETRY]: '⌯',        // U+232F (deprecated)

    // Runout
    [GDTCharacteristicType.CIRCULAR_RUNOUT]: '↗', // Özel çizim gerekir
    [GDTCharacteristicType.TOTAL_RUNOUT]: '⇗'     // Özel çizim gerekir
};

/**
 * GD&T Türkçe İsimler
 */
export const GDT_NAMES_TR: Record<GDTCharacteristicType, string> = {
    [GDTCharacteristicType.FLATNESS]: 'Düzlük',
    [GDTCharacteristicType.STRAIGHTNESS]: 'Doğruluk',
    [GDTCharacteristicType.CIRCULARITY]: 'Dairesellik',
    [GDTCharacteristicType.CYLINDRICITY]: 'Silindiriklik',
    [GDTCharacteristicType.PROFILE_OF_LINE]: 'Çizgi Profili',
    [GDTCharacteristicType.PROFILE_OF_SURFACE]: 'Yüzey Profili',
    [GDTCharacteristicType.ANGULARITY]: 'Açısallık',
    [GDTCharacteristicType.PERPENDICULARITY]: 'Diklik',
    [GDTCharacteristicType.PARALLELISM]: 'Paralellik',
    [GDTCharacteristicType.POSITION]: 'Konum',
    [GDTCharacteristicType.CONCENTRICITY]: 'Eş Merkezlilik',
    [GDTCharacteristicType.SYMMETRY]: 'Simetri',
    [GDTCharacteristicType.CIRCULAR_RUNOUT]: 'Dairesel Kaçıklık',
    [GDTCharacteristicType.TOTAL_RUNOUT]: 'Toplam Kaçıklık'
};

/**
 * GD&T İngilizce İsimler
 */
export const GDT_NAMES_EN: Record<GDTCharacteristicType, string> = {
    [GDTCharacteristicType.FLATNESS]: 'Flatness',
    [GDTCharacteristicType.STRAIGHTNESS]: 'Straightness',
    [GDTCharacteristicType.CIRCULARITY]: 'Circularity',
    [GDTCharacteristicType.CYLINDRICITY]: 'Cylindricity',
    [GDTCharacteristicType.PROFILE_OF_LINE]: 'Profile of a Line',
    [GDTCharacteristicType.PROFILE_OF_SURFACE]: 'Profile of a Surface',
    [GDTCharacteristicType.ANGULARITY]: 'Angularity',
    [GDTCharacteristicType.PERPENDICULARITY]: 'Perpendicularity',
    [GDTCharacteristicType.PARALLELISM]: 'Parallelism',
    [GDTCharacteristicType.POSITION]: 'Position',
    [GDTCharacteristicType.CONCENTRICITY]: 'Concentricity',
    [GDTCharacteristicType.SYMMETRY]: 'Symmetry',
    [GDTCharacteristicType.CIRCULAR_RUNOUT]: 'Circular Runout',
    [GDTCharacteristicType.TOTAL_RUNOUT]: 'Total Runout'
};

// ====================================================================
// MATERIAL CONDITION MODIFIERS
// ====================================================================

/**
 * Malzeme Durumu Modifierleri - ASME Y14.5-2018
 */
export enum MaterialCondition {
    /** Yok - Varsayılan */
    NONE = 'NONE',

    /** Regardless of Feature Size - Boyuttan bağımsız (default) */
    RFS = 'RFS',

    /** Maximum Material Condition - Maksimum malzeme durumu */
    MMC = 'MMC',

    /** Least Material Condition - Minimum malzeme durumu */
    LMC = 'LMC',

    /** Regardless of Material Boundary - ASME Y14.5-2018 yeni */
    RMB = 'RMB',

    /** Maximum Material Boundary - ASME Y14.5-2018 yeni */
    MMB = 'MMB',

    /** Least Material Boundary - ASME Y14.5-2018 yeni */
    LMB = 'LMB'
}

/**
 * Material Condition Sembolleri
 */
export const MATERIAL_CONDITION_SYMBOLS: Record<MaterialCondition, string> = {
    [MaterialCondition.NONE]: '',     // Yok
    [MaterialCondition.RFS]: '',      // Varsayılan, sembol yok (ASME Y14.5-2018)
    [MaterialCondition.MMC]: 'Ⓜ',    // Daire içinde M
    [MaterialCondition.LMC]: 'Ⓛ',    // Daire içinde L
    [MaterialCondition.RMB]: '',      // Sembol yok
    [MaterialCondition.MMB]: 'Ⓜ',    // MMC ile aynı
    [MaterialCondition.LMB]: 'Ⓛ'     // LMC ile aynı
};

// ====================================================================
// TOLERANCE ZONE MODIFIERS
// ====================================================================

/**
 * Tolerans Bölgesi Modifierleri
 */
export enum ToleranceZoneModifier {
    /** Varsayılan - modifier yok */
    NONE = 'NONE',

    /** Çap - Dairesel/silindirik tolerans bölgesi */
    DIAMETER = 'DIAMETER',

    /** Küresel çap */
    SPHERICAL_DIAMETER = 'SPHERICAL_DIAMETER',

    /** Yarıçap */
    RADIUS = 'RADIUS',

    /** Küresel yarıçap */
    SPHERICAL_RADIUS = 'SPHERICAL_RADIUS',

    /** Projected tolerance zone */
    PROJECTED = 'PROJECTED',

    /** Free state */
    FREE_STATE = 'FREE_STATE',

    /** Tangent plane */
    TANGENT_PLANE = 'TANGENT_PLANE',

    /** Unequally disposed - ASME Y14.5-2018 yeni */
    UNEQUALLY_DISPOSED = 'UNEQUALLY_DISPOSED',

    /** İstatistiksel tolerans */
    STATISTICAL = 'STATISTICAL'
}

/**
 * Tolerans Bölgesi Modifier Sembolleri
 */
export const TOLERANCE_ZONE_SYMBOLS: Record<ToleranceZoneModifier, string> = {
    [ToleranceZoneModifier.NONE]: '',
    [ToleranceZoneModifier.DIAMETER]: '⌀',
    [ToleranceZoneModifier.SPHERICAL_DIAMETER]: 'S⌀',
    [ToleranceZoneModifier.RADIUS]: 'R',
    [ToleranceZoneModifier.SPHERICAL_RADIUS]: 'SR',
    [ToleranceZoneModifier.PROJECTED]: 'Ⓟ',
    [ToleranceZoneModifier.FREE_STATE]: 'Ⓕ',
    [ToleranceZoneModifier.TANGENT_PLANE]: 'Ⓣ',
    [ToleranceZoneModifier.UNEQUALLY_DISPOSED]: 'Ⓤ',
    [ToleranceZoneModifier.STATISTICAL]: '<ST>'
};

// ====================================================================
// DATUM DEFINITIONS
// ====================================================================

/**
 * Datum Referans Tipi
 */
export enum DatumType {
    /** Tekil datum */
    SINGLE = 'SINGLE',

    /** Birleşik datum (örn: A-B) */
    COMMON = 'COMMON',

    /** Datum hedef */
    TARGET = 'TARGET'
}

/**
 * Datum Referans
 */
export interface DatumReference {
    /** Datum harfi (A, B, C, ...) */
    letter: string;

    /** Datum tipi (opsiyonel, varsayılan SINGLE) */
    type?: DatumType;

    /** Malzeme durumu (opsiyonel) */
    materialCondition?: MaterialCondition;

    /** Birleşik datum için ikincil harf */
    secondaryLetter?: string;
}

/**
 * Datum Feature bilgisi
 */
export interface DatumFeatureInfo {
    /** Benzersiz ID */
    id: string;

    /** Datum harfi */
    letter: string;

    /** Datum tipi */
    type: DatumType;

    /** 3D konum */
    position: THREE.Vector3;

    /** Normal yön (yüzey için) */
    normal?: THREE.Vector3;

    /** Bağlı geometri ID */
    geometryId?: string;

    /** Yüzey tipi */
    surfaceType?: 'PLANE' | 'CYLINDER' | 'CONE' | 'SPHERE' | 'COMPLEX';

    /** Açıklama */
    description?: string;
}

// ====================================================================
// FEATURE CONTROL FRAME
// ====================================================================

/**
 * Feature Control Frame bileşeni - Tek tolerans satırı
 */
export interface FCFComponent {
    /** Geometrik karakteristik */
    characteristic: GDTCharacteristicType;

    /** Tolerans bölgesi modifierı (opsiyonel, başta) */
    zoneModifier?: ToleranceZoneModifier;

    /** Tolerans bölgesi modifierı (alias) */
    toleranceZoneModifier?: ToleranceZoneModifier;

    /** Tolerans değeri */
    toleranceValue: number;

    /** Birim */
    unit?: 'mm' | 'inch';

    /** Tolerans malzeme durumu */
    materialCondition?: MaterialCondition;

    /** Tolerans malzeme durumu (alias) */
    toleranceMaterialCondition?: MaterialCondition;

    /** Birincil datum referansı */
    primaryDatum?: DatumReference;

    /** İkincil datum referansı */
    secondaryDatum?: DatumReference;

    /** Üçüncül datum referansı */
    tertiaryDatum?: DatumReference;

    /** Datum referansları listesi */
    datumReferences?: DatumReference[];
}

/**
 * Feature Control Frame konfigürasyonu
 * Birden fazla tolerans satırı içerebilir (composite tolerance)
 */
export interface FeatureControlFrameConfig {
    /** Benzersiz ID */
    id?: string;

    /** İsim */
    name?: string;

    /** 3D konum */
    position: THREE.Vector3;

    /** Tolerans bileşenleri (satırlar) */
    components: FCFComponent[];

    /** Leader line hedef noktası (opsiyonel) */
    targetPoint?: THREE.Vector3;

    /** Görünüm ayarları */
    style?: FCFStyleConfig;

    /** Sabit boyut (zoom'a göre scale) */
    sabitBoyut?: boolean;
}

/**
 * FCF Görünüm Ayarları
 */
export interface FCFStyleConfig {
    /** Çerçeve rengi */
    frameColor?: string;

    /** Çerçeve rengi (alias) */
    borderColor?: string;

    /** Metin rengi */
    textColor?: string;

    /** Arka plan rengi */
    backgroundColor?: string;

    /** Çerçeve kalınlığı */
    frameWidth?: number;

    /** Çizgi kalınlığı (alias) */
    lineWidth?: number;

    /** Font boyutu */
    fontSize?: number;

    /** Font ailesi */
    fontFamily?: string;

    /** Sabit boyut (zoom'a göre scale) */
    fixedSize?: boolean;

    /** Leader çizgi rengi */
    leaderColor?: string;

    /** Leader çizgi kalınlığı */
    leaderWidth?: number;

    /** Leader tipi */
    leaderType?: 'solid' | 'dashed' | 'none';
}

// ====================================================================
// COMPOSITE TOLERANCE
// ====================================================================

/**
 * Composite Tolerans Tipi
 * Position için PLTZF (Pattern-Locating Tolerance Zone Framework)
 * ve FRTZF (Feature-Relating Tolerance Zone Framework)
 */
export interface CompositeToleranceConfig extends FeatureControlFrameConfig {
    /** Composite tip */
    compositeType: 'PATTERN_LOCATING' | 'FEATURE_RELATING';

    /** İkinci tolerans satırı (feature-relating) */
    secondaryComponent: FCFComponent;
}

// ====================================================================
// DIMENSION TYPES
// ====================================================================

/**
 * Boyut Tipi
 */
export enum DimensionType {
    /** Lineer mesafe */
    LINEAR = 'LINEAR',

    /** Çap */
    DIAMETER = 'DIAMETER',

    /** Yarıçap */
    RADIUS = 'RADIUS',

    /** Açı */
    ANGULAR = 'ANGULAR',

    /** Yay uzunluğu */
    ARC_LENGTH = 'ARC_LENGTH',

    /** Ordinate */
    ORDINATE = 'ORDINATE'
}

/**
 * Boyut konfigürasyonu
 */
export interface DimensionConfig {
    /** Boyut tipi */
    type: DimensionType;

    /** Nominal değer */
    nominalValue: number;

    /** Üst tolerans (+ yönde) */
    upperTolerance?: number;

    /** Alt tolerans (- yönde) */
    lowerTolerance?: number;

    /** Birim */
    unit?: 'mm' | 'inch';

    /** Temel boyut (Basic dimension) */
    isBasic?: boolean;

    /** Referans boyut */
    isReference?: boolean;

    /** Minimum boyut */
    isMinimum?: boolean;

    /** Maksimum boyut */
    isMaximum?: boolean;
}

// ====================================================================
// SURFACE TEXTURE (Yüzey Kalitesi)
// ====================================================================

/**
 * Yüzey Kalitesi Parametresi
 */
export enum SurfaceTextureParameter {
    /** Ortalama pürüzlülük */
    RA = 'Ra',

    /** Pürüzlülük yüksekliği */
    RZ = 'Rz',

    /** Maksimum pürüzlülük yüksekliği */
    RMAX = 'Rmax',

    /** Toplam pürüzlülük yüksekliği */
    RT = 'Rt',

    /** Waviness height */
    WT = 'Wt'
}

/**
 * İşlem Tipi
 */
export enum MachiningProcess {
    /** Herhangi bir işlem */
    ANY = 'ANY',

    /** Malzeme kaldırma gerekli */
    MATERIAL_REMOVAL_REQUIRED = 'MRR',

    /** Malzeme kaldırma yasak */
    MATERIAL_REMOVAL_PROHIBITED = 'MRP',

    /** Talaşlı işlem */
    MACHINED = 'MACHINED',

    /** Taşlama */
    GROUND = 'GROUND',

    /** Honlama */
    HONED = 'HONED',

    /** Lepleme */
    LAPPED = 'LAPPED',

    /** Parlatma */
    POLISHED = 'POLISHED',

    /** Döküm */
    CAST = 'CAST',

    /** Dövme */
    FORGED = 'FORGED'
}

/**
 * Yüzey Kalitesi Konfigürasyonu
 */
export interface SurfaceTextureConfig {
    /** Parametre tipi */
    parameter: SurfaceTextureParameter;

    /** Değer (mikrometre) */
    value: number;

    /** Üst limit (opsiyonel) */
    upperLimit?: number;

    /** İşlem tipi */
    process?: MachiningProcess;

    /** Lay direction (yön) */
    layDirection?: '=' | '⊥' | 'X' | 'M' | 'C' | 'R' | 'P';

    /** Sampling length */
    samplingLength?: number;

    /** Konum */
    position: THREE.Vector3;
}

// ====================================================================
// PMI (Product Manufacturing Information)
// ====================================================================

/**
 * PMI Eleman Tipi
 */
export enum PMIElementType {
    /** Feature Control Frame */
    FEATURE_CONTROL_FRAME = 'FCF',

    /** Datum Feature */
    DATUM_FEATURE = 'DATUM',

    /** Datum Target */
    DATUM_TARGET = 'DATUM_TARGET',

    /** Boyut */
    DIMENSION = 'DIMENSION',

    /** Yüzey Kalitesi */
    SURFACE_TEXTURE = 'SURFACE_TEXTURE',

    /** Not */
    NOTE = 'NOTE',

    /** Kaynak Sembolü */
    WELD_SYMBOL = 'WELD',

    /** Flag/İşaret */
    FLAG = 'FLAG'
}

/**
 * PMI Görünüm (Saved View)
 */
export interface PMISavedView {
    /** Görünüm ID */
    id: string;

    /** Görünüm adı */
    name: string;

    /** Kamera konumu */
    cameraPosition: THREE.Vector3;

    /** Kamera hedefi */
    cameraTarget: THREE.Vector3;

    /** Kamera yukarı vektörü */
    cameraUp: THREE.Vector3;

    /** Zoom seviyesi */
    zoom: number;

    /** Bu görünümde görünen PMI ID'leri */
    visiblePMIIds: string[];
}

/**
 * PMI Veri Modeli
 */
export interface PMIData {
    /** Benzersiz ID */
    id: string;

    /** PMI tipi */
    type: PMIElementType;

    /** İsim */
    name?: string;

    /** 3D konum */
    position: THREE.Vector3;

    /** Bağlı geometri ID'leri */
    associatedGeometryIds?: string[];

    /** FCF verisi (FCF için) */
    fcfData?: FeatureControlFrameConfig;

    /** Datum verisi (Datum için) */
    datumData?: DatumFeatureInfo;

    /** Boyut verisi (Dimension için) */
    dimensionData?: DimensionConfig;

    /** Yüzey kalitesi verisi */
    surfaceTextureData?: SurfaceTextureConfig;

    /** Not metni */
    noteText?: string;

    /** Oluşturulma tarihi */
    createdAt?: Date;

    /** Güncellenme tarihi */
    updatedAt?: Date;

    /** Kullanıcı verileri */
    userData?: Record<string, any>;
}

/**
 * PMI Tablosu - Bir sahne için tüm PMI verileri
 */
export interface PMITable {
    /** Sahne/Model ID */
    modelId: string;

    /** PMI elemanları */
    elements: PMIData[];

    /** Kaydedilmiş görünümler */
    savedViews: PMISavedView[];

    /** Metadata */
    metadata?: {
        standard?: 'ASME' | 'ISO';
        version?: string;
        author?: string;
        createdAt?: Date;
    };
}

// ====================================================================
// VALIDATION
// ====================================================================

/**
 * GD&T Doğrulama Sonucu
 */
export interface GDTValidationResult {
    /** Geçerli mi? */
    isValid: boolean;

    /** Hatalar */
    errors: GDTValidationError[];

    /** Uyarılar */
    warnings: GDTValidationWarning[];
}

/**
 * Doğrulama Hatası
 */
export interface GDTValidationError {
    /** Hata kodu */
    code: string;

    /** Hata mesajı */
    message: string;

    /** İlgili eleman ID */
    elementId?: string;

    /** Önerilen düzeltme */
    suggestion?: string;
}

/**
 * Doğrulama Uyarısı
 */
export interface GDTValidationWarning {
    /** Uyarı kodu */
    code: string;

    /** Uyarı mesajı */
    message: string;

    /** İlgili eleman ID */
    elementId?: string;
}

// ====================================================================
// HELPER FUNCTIONS
// ====================================================================

/**
 * Karakteristik datum gerektiriyor mu?
 */
export function requiresDatum(characteristic: GDTCharacteristicType): boolean {
    const noDatumRequired: GDTCharacteristicType[] = [
        GDTCharacteristicType.FLATNESS,
        GDTCharacteristicType.STRAIGHTNESS,
        GDTCharacteristicType.CIRCULARITY,
        GDTCharacteristicType.CYLINDRICITY
    ];
    return !noDatumRequired.includes(characteristic);
}

/**
 * Karakteristik kategorisini al
 */
export function getCharacteristicCategory(
    characteristic: GDTCharacteristicType
): 'FORM' | 'PROFILE' | 'ORIENTATION' | 'LOCATION' | 'RUNOUT' {
    switch (characteristic) {
        case GDTCharacteristicType.FLATNESS:
        case GDTCharacteristicType.STRAIGHTNESS:
        case GDTCharacteristicType.CIRCULARITY:
        case GDTCharacteristicType.CYLINDRICITY:
            return 'FORM';

        case GDTCharacteristicType.PROFILE_OF_LINE:
        case GDTCharacteristicType.PROFILE_OF_SURFACE:
            return 'PROFILE';

        case GDTCharacteristicType.ANGULARITY:
        case GDTCharacteristicType.PERPENDICULARITY:
        case GDTCharacteristicType.PARALLELISM:
            return 'ORIENTATION';

        case GDTCharacteristicType.POSITION:
        case GDTCharacteristicType.CONCENTRICITY:
        case GDTCharacteristicType.SYMMETRY:
            return 'LOCATION';

        case GDTCharacteristicType.CIRCULAR_RUNOUT:
        case GDTCharacteristicType.TOTAL_RUNOUT:
            return 'RUNOUT';

        default:
            return 'FORM';
    }
}

/**
 * Karakteristik deprecated mı?
 */
export function isDeprecated(characteristic: GDTCharacteristicType): boolean {
    return characteristic === GDTCharacteristicType.CONCENTRICITY ||
           characteristic === GDTCharacteristicType.SYMMETRY;
}

/**
 * Tolerans değerini formatla
 */
export function formatToleranceValue(
    value: number,
    unit: 'mm' | 'inch' = 'mm',
    decimals: number = 3
): string {
    const formatted = value.toFixed(decimals);
    return unit === 'inch' ? formatted : formatted;
}

/**
 * FCF text oluştur (örn: "⌖ ⌀0.25 Ⓜ A B C")
 */
export function generateFCFText(component: FCFComponent): string {
    const parts: string[] = [];

    // Sembol
    parts.push(GDT_SYMBOLS[component.characteristic]);

    // Tolerans bölgesi modifier (varsa)
    if (component.zoneModifier && component.zoneModifier !== ToleranceZoneModifier.NONE) {
        parts.push(TOLERANCE_ZONE_SYMBOLS[component.zoneModifier]);
    }

    // Tolerans değeri
    parts.push(formatToleranceValue(component.toleranceValue, component.unit));

    // Material condition (varsa)
    if (component.toleranceMaterialCondition &&
        component.toleranceMaterialCondition !== MaterialCondition.RFS) {
        parts.push(MATERIAL_CONDITION_SYMBOLS[component.toleranceMaterialCondition]);
    }

    // Datumlar
    if (component.primaryDatum) {
        parts.push(formatDatumReference(component.primaryDatum));
    }
    if (component.secondaryDatum) {
        parts.push(formatDatumReference(component.secondaryDatum));
    }
    if (component.tertiaryDatum) {
        parts.push(formatDatumReference(component.tertiaryDatum));
    }

    return parts.join(' ');
}

/**
 * Datum referansını formatla
 */
function formatDatumReference(datum: DatumReference): string {
    let result = datum.letter;

    if (datum.type === DatumType.COMMON && datum.secondaryLetter) {
        result += `-${datum.secondaryLetter}`;
    }

    if (datum.materialCondition && datum.materialCondition !== MaterialCondition.RFS) {
        result += MATERIAL_CONDITION_SYMBOLS[datum.materialCondition];
    }

    return result;
}
